import React from "react";
import { BankAccountsTotalCard } from "./BankAccountsTotalCard";
import { BankAccountCard, type BankAccountCardProps } from "./BankAccountCard";
import { FixedDepositCard } from "./FixedDepositCard";
import { Skeleton } from "../shared/Skeleton";
import { useAllBankAccounts } from "../../hooks/useAllBankAccounts";
import { formatIndianAmount } from "../../utils/formatPlAmount";

const RING_COLORS: Array<BankAccountCardProps["ringColor"]> = [
  "orange",
  "blue",
  "neutral",
];

/** Skeleton matching BankAccountsTotalCard layout (label + amount + account count). */
function BankAccountsTotalCardSkeleton() {
  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-lg bg-default-background px-6 py-5 shadow-sm">
      <Skeleton className="h-3 w-40 rounded" />
      <div className="flex w-full items-end justify-between">
        <Skeleton className="h-10 w-48 rounded" />
        <Skeleton className="h-4 w-28 rounded" />
      </div>
    </div>
  );
}

/** Skeleton matching BankAccountCard layout (circle + name/balance/trend). */
function BankAccountCardSkeleton() {
  return (
    <div
      className="flex shrink-0 items-center gap-4 rounded-lg bg-default-background px-6 py-5 shadow-sm"
      style={{ width: 320, minWidth: 320 }}
    >
      <Skeleton className="h-16 w-16 shrink-0 rounded-full" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-32 rounded" />
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>
        <Skeleton className="h-8 w-24 rounded" />
        <Skeleton className="h-4 w-24 rounded" />
      </div>
    </div>
  );
}

/** Skeleton matching FixedDepositCard layout (label, title, subtitle | amount, badge). */
function FixedDepositCardSkeleton() {
  return (
    <div className="flex w-full items-start justify-between rounded-lg bg-default-background px-6 py-5 shadow-sm">
      <div className="flex flex-col items-start gap-1">
        <Skeleton className="h-3 w-24 rounded" />
        <Skeleton className="h-7 w-44 rounded" />
        <Skeleton className="h-4 w-36 rounded" />
      </div>
      <div className="flex flex-col items-end gap-2">
        <Skeleton className="h-7 w-28 rounded" />
        <Skeleton className="h-6 w-14 rounded-full" />
      </div>
    </div>
  );
}

export function BankAccountsSection() {
  const {
    totalAvailableCash,
    bankAccounts,
    fixedDeposits,
    loading,
    error,
    refetch,
  } = useAllBankAccounts();

  const totalFormatted = formatIndianAmount(totalAvailableCash);
  const accountCount = bankAccounts.length;
  const asOfLabel = new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());
  const subtitle = `Cash distribution across accounts as of ${asOfLabel}`;

  if (loading) {
    return (
      <div className="flex w-full flex-col items-start gap-6 rounded-lg bg-neutral-100 px-8 py-8">
        <div className="flex w-full flex-col items-start gap-1">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Current Bank Balance
          </span>
          <span className="text-caption font-caption text-subtext-color">
            {subtitle}
          </span>
        </div>
        <BankAccountsTotalCardSkeleton />
        <div className="flex w-full items-start gap-4 overflow-x-auto pb-2">
          <BankAccountCardSkeleton />
          <BankAccountCardSkeleton />
          <BankAccountCardSkeleton />
        </div>
        <FixedDepositCardSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex w-full flex-col items-start gap-6 rounded-lg bg-neutral-100 px-8 py-8">
        <div className="flex w-full flex-col items-start gap-1">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Current Bank Balance
          </span>
          <span className="text-caption font-caption text-subtext-color">
            {subtitle}
          </span>
        </div>
        <div className="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-body font-body text-error-700">
          {error}
          <button
            type="button"
            onClick={refetch}
            className="ml-2 underline focus:outline-none"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const percentFor = (balance: number) =>
    totalAvailableCash > 0 ? (balance / totalAvailableCash) * 100 : 0;

  return (
    <div className="flex w-full flex-col items-start gap-6 rounded-lg bg-neutral-100 px-8 py-8">
      <div className="flex w-full flex-col items-start gap-1">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Current Bank Balance
        </span>
        <span className="text-caption font-caption text-subtext-color">
          {subtitle}
        </span>
      </div>
      <BankAccountsTotalCard
        totalAmount={totalFormatted}
        accountCount={accountCount}
      />
      <div className="flex w-full items-start gap-4 overflow-x-auto pb-2">
        {bankAccounts.map((acc, i) => {
          const cardProps: BankAccountCardProps = {
            name: acc.bankName,
            balance: formatIndianAmount(acc.balance),
            percent: percentFor(acc.balance),
            trend: "neutral",
            isConnected: acc.isActive,
            ringColor: RING_COLORS[i % RING_COLORS.length],
            pendingTransactionsCount: acc.pendingTransactionsCount,
          };
          return <BankAccountCard key={acc.id} {...cardProps} />;
        })}
      </div>
      {/* {fixedDeposits.map((fd) => (
        <FixedDepositCard
          key={fd.id}
          title={fd.bankName}
          amount={formatIndianAmount(fd.balance)}
          status={fd.isActive ? "Active" : "Inactive"}
        />
      ))} */}
    </div>
  );
}
